/**
 * Headlight Platform Configuration — Master List seed catalog.
 *
 * The reference / catalog "master lists" an administrator curates. Each entry is a
 * thin, data-driven description of a Meadow entity to expose as a managed list; the
 * MasterList-Builder turns each into a pict-section-recordset configuration plus a
 * navigation entry. Columns and the create / edit / view forms are auto-generated from
 * the entity's /1.0/<Entity>/Schema endpoint — no hand-written manifest required.
 *
 * This static seed is the always-present baseline. The MasterListRegistry provider layers
 * the cascading Configuration service on top of it (namespace 'MasterListCatalog', one value
 * per entity, merged Global -> Customer -> Project -> User) and a GUI tool edits it, so product
 * managers can add and tune lists — and choose each list's association views — without a
 * developer. Entries here are the platform-default fallback when no config layer overrides them.
 *
 * Per entry:
 *   Entity       (required) — the Meadow entity / record set name (e.g. 'Material').
 *   Label        — human navigation label (defaults to Entity).
 *   Domain       — grouping bucket for the future nav retool (informational for now).
 *   Icon         — Font Awesome class for the nav entry.
 *   Keywords     — search terms for the navigation palette.
 *   Description  — one-line nav description.
 *   SearchFields — optional list-search fields (defaults to [ 'Name' ]).
 *   Columns      — optional curated columns; omit to auto-generate from the schema.
 *   DefaultFilter — optional FoxHound filter string (e.g. a 'FSF~CreateDate~DESC~0' sort) for a bare
 *                  /PSRS/<Entity>/List route, the one workspace Back links and redirects land on.
 *   Sortable     — optional; true makes the list's column headers sort it (pict-section-recordset
 *                  RecordSetListSortable). A column can opt out with Sortable: false.
 *   QuickFilters — optional quick-filter bar fields (pict-section-recordset QuickFilters: a field name
 *                  or { Field, Label }). Quick filters are opt-in here, so a list without this has no bar.
 *   Filters      — optional per-field filter tweaks merged onto an auto-generated clause (e.g. a
 *                  foreign-key picker's ExternalFilterByColumns), passed through to the recordset provider.
 *   FieldFilterClauses — optional per-field REPLACEMENT clause arrays; the module drops its
 *                  auto-generated clauses for that field and uses these instead (e.g. a status pick list).
 *
 * Connection surfaces (ReadTabs / ChildLists / Matrix / Bulk / Unlink) are NOT hand-written here — they
 * are merged in per entity from Connections.generated.js (derived from the schema graph by
 * build-connections.js), unless an entry hand-specifies that array. Regenerate the connection registry
 * with:  node source/master-lists/build-connections.js
 */

const { EntityConnections } = require('./Connections.generated.js');

/**
 * Merge an entity's generated connection defaults (ReadTabs / ChildLists / Matrix / Bulk / Unlink) into
 * its catalog entry, unless the entry already hand-specifies that array (a hand array is an intentional
 * override — including an empty one meaning "show none").
 * @param {Object} pEntry - a master-list catalog entry.
 * @return {Object} the entry with connection defaults applied.
 */
function applyConnectionDefaults(pEntry)
{
	const tmpConnections = EntityConnections[pEntry.Entity];
	if (!tmpConnections) { return pEntry; }
	const tmpResult = Object.assign({}, pEntry);
	[ 'ReadTabs', 'ChildLists', 'MatrixAssociations', 'BulkAssociations', 'UnlinkAssociations' ].forEach((pKey) =>
	{
		if (!Array.isArray(tmpResult[pKey]) && Array.isArray(tmpConnections[pKey]) && (tmpConnections[pKey].length > 0))
		{
			tmpResult[pKey] = tmpConnections[pKey].slice();
		}
	});
	return tmpResult;
}

const CATALOG =
[
	{
		Entity: 'Material',
		Label: 'Materials',
		Domain: 'Materials & Mixes',
		Icon: 'fas fa-cube',
		Keywords: 'material catalog aggregate concrete asphalt base source',
		Description: 'Base material catalog — aggregates, mixes, and source materials.',
		Sortable: true,
		// Search the code columns too, not just Name — users routinely look a material up by its
		// code (Material Code / Catalog Code / Item Code), which the default [ 'Name' ] never matched.
		SearchFields: [ 'Name', 'NameAbbreviated', 'MaterialCode', 'CatalogCode', 'ItemCode' ],
		// Bespoke read tab: the specification sets reachable transitively through this material's specs —
		// the reciprocal of TestSpecificationSet's SpecSetMaterials tab (B5).
		ViewTabs: [ { View: 'MaterialSpecSets', Title: 'Test Specification Sets' } ]
	},
	{
		Entity: 'Product',
		Label: 'Products',
		Domain: 'Products & Production',
		Icon: 'fas fa-box',
		Keywords: 'product manufactured producer finished goods',
		Description: 'Manufactured products linked to base materials and producers.'
	},
	{
		Entity: 'ProductionPlant',
		Label: 'Production Plants',
		Domain: 'Products & Production',
		Icon: 'fas fa-industry',
		Keywords: 'plant producer facility manufacturing site',
		Description: 'Producer manufacturing facilities and their addresses.'
	},
	{
		Entity: 'PayItem',
		Label: 'Pay Items',
		Domain: 'Pay & Funding',
		Icon: 'fas fa-dollar-sign',
		Keywords: 'pay item bid payment specification billing',
		Description: 'Bid / payment items and their linked test specification sets.'
	},
	{
		Entity: 'Lab',
		Label: 'Labs',
		Domain: 'Labs & Equipment',
		Icon: 'fas fa-flask',
		Keywords: 'lab laboratory testing facility',
		Description: 'Testing laboratories — facilities, addresses, and hierarchy.',
		Sortable: true
	},
	{
		Entity: 'TestSpecification',
		Label: 'Test Specifications',
		Domain: 'Tests & Specifications',
		Icon: 'fas fa-vial',
		Keywords: 'test specification AASHTO ASTM requirement threshold',
		Description: 'Individual test specifications within specification sets.'
	},

	// --- The rest of the home app's (app_headlight) Master Lists section ---
	// Source of truth: lib/bootstraps/mixins/modules.js (CustomerConfig.MasterLists + Entities,
	// minus Role / Contract / User) with labels from lib/bootstraps/mixins/entities.js EntityConfig.
	{
		Entity: 'Equipment',
		Label: 'Equipment',
		Domain: 'Labs & Equipment',
		Icon: 'fas fa-screwdriver-wrench',
		Keywords: 'equipment machinery field plant',
		Description: 'Field equipment and machinery.',
		Sortable: true
	},
	{
		Entity: 'Location',
		Label: 'Locations',
		Domain: 'Labs & Equipment',
		Icon: 'fas fa-location-dot',
		Keywords: 'location storage room shelf freezer building sample receive scan qr',
		Description: 'Places samples are kept — buildings, rooms, shelves — and which is inside which.',
		Sortable: true,
		SearchFields: [ 'Name', 'QRCode', 'Type', 'Description' ],
		QuickFilters: [ 'Name', { Field: 'CreateDate', Label: 'Added' } ],
		Columns:
		[
			{ Key: 'Name', DisplayName: 'Name' },
			{ Key: 'Type', DisplayName: 'Type' },
			{ Key: 'QRCode', DisplayName: 'QR Code' },
			{ Key: 'Description', DisplayName: 'Description' }
		]
	},
	{
		Entity: 'Team',
		Label: 'Teams',
		Domain: 'Organizations & People',
		Icon: 'fas fa-users',
		Keywords: 'team crew squad members roles enrollment shared ownership collaboration people manager document access',
		Description: 'Groups of people, each in a role, that can be granted documents together. Members reach a granted document, and its project, without being added to the project.'
	},
	{
		Entity: 'Contact',
		Label: 'Personnel',
		Domain: 'Organizations & People',
		Icon: 'fas fa-address-card',
		Keywords: 'contact personnel people person staff',
		Description: 'Personnel and contacts linked to organizations.'
	},
	{
		Entity: 'CertificationType',
		Label: 'Certifications',
		Domain: 'Organizations & People',
		Icon: 'fas fa-stamp',
		Keywords: 'certification type credential DBE qualification',
		Description: 'Certification types and credentials.'
	},
	{
		Entity: 'Account',
		Label: 'Accounts',
		Domain: 'Pay & Funding',
		Icon: 'fas fa-money-check-dollar',
		Keywords: 'account financial ledger budget',
		Description: 'Financial accounts.'
	},
	{
		Entity: 'FundingSource',
		Label: 'Funding Sources',
		Domain: 'Pay & Funding',
		Icon: 'fas fa-building-columns',
		Keywords: 'funding source federal state grant loan',
		Description: 'Funding sources for projects and contracts.'
	},
	{
		Entity: 'Fund',
		Label: 'Funds',
		Domain: 'Pay & Funding',
		Icon: 'fas fa-piggy-bank',
		Keywords: 'fund balance cost center appropriation money pool',
		Description: 'Funds a funding source draws from (each carries a balance).'
	},
	{
		Entity: 'FundingCategory',
		Label: 'Funding Categories',
		Domain: 'Pay & Funding',
		Icon: 'fas fa-folder-tree',
		Keywords: 'funding category bucket classification participating non-participating',
		Description: 'Categories that classify how funding applies to line items.'
	},
	{
		Entity: 'AccountType',
		Label: 'Account Types',
		Domain: 'Pay & Funding',
		Icon: 'fas fa-list-ul',
		Keywords: 'account type kind ledger classification',
		Description: 'The kinds of financial account (drives what each account applies to).'
	},
	{
		Entity: 'MixDesign',
		Label: 'Mix Designs',
		Domain: 'Materials & Mixes',
		Icon: 'fas fa-flask-vial',
		Keywords: 'mix design formulation plant concrete asphalt',
		Description: 'Mix design formulations.'
	},
	{
		Entity: 'MixType',
		Label: 'Mix Types',
		Domain: 'Materials & Mixes',
		Icon: 'fas fa-layer-group',
		Keywords: 'mix type category classification asphalt concrete nominal aggregate traffic',
		Description: 'Mix type categories and their per-customer metadata.'
	},
	{
		Entity: 'MixSpecification',
		Label: 'Mix Specifications',
		Domain: 'Materials & Mixes',
		Icon: 'fas fa-file-lines',
		Keywords: 'mix specification requirement template',
		Description: 'Mix specification templates.'
	},
	{
		Entity: 'Lot',
		Label: 'Lots',
		Domain: 'Materials & Mixes',
		Icon: 'fas fa-boxes-stacked',
		Keywords: 'lot batch material placement',
		Description: 'Material lots and batches.'
	},
	{
		Entity: 'ProjectOffice',
		Label: 'Project Offices',
		Domain: 'Projects & Contracts',
		Icon: 'fas fa-building',
		Keywords: 'project office district region',
		Description: 'Regional and district project offices.'
	},
	{
		Entity: 'TestSpecificationSet',
		Label: 'Test Specification Sets',
		Domain: 'Tests & Specifications',
		Icon: 'fas fa-vials',
		Keywords: 'test specification set group AASHTO ASTM',
		Description: 'Grouped sets of test specifications.',
		// Bespoke read tab: the materials reachable transitively through this set's specs (B5).
		ViewTabs: [ { View: 'SpecSetMaterials', Title: 'Materials' } ]
	},
	{
		Entity: 'Test',
		Label: 'Material Sample Tests',
		Domain: 'Tests & Specifications',
		Icon: 'fas fa-flask',
		Keywords: 'test material sample method procedure',
		Description: 'Material sample test definitions.',
		Sortable: true
	},
	{
		Entity: 'TestInstance',
		Label: 'Test Instances',
		Domain: 'Tests & Specifications',
		Icon: 'fas fa-microscope',
		Keywords: 'test instance result run reading',
		Description: 'Recorded test instances and results.'
	},
	{
		Entity: 'Sample',
		Label: 'Samples',
		Domain: 'Tests & Specifications',
		Icon: 'fas fa-vial',
		Keywords: 'sample field material specimen',
		Description: 'Field and material samples.',
		// The identifiers people search by: Description, PSID, sample ID (QR Code) and Lab Reference #.
		SearchFields: [ 'Name', 'LabControlNumber', 'QRCode', 'LabReferenceNumber' ],
		// Newest first, matching the Samples menu route (navigation-graph.js and web/lims LIMS-Navigation-Graph.js).
		DefaultFilter: 'FSF~CreateDate~DESC~0',
		Sortable: true,
		// Status stays in the drawer's labelled pick list: the quick bar can show it only as raw codes (PASS, LOGD) or a free-text box.
		QuickFilters: [ { Field: 'Name', Label: 'Description' }, 'IDProject', 'IDMaterial', { Field: 'CreateDate', Label: 'Logged' }, { Field: 'CreatingIDUser', Label: 'Logged by' } ],
		// Find samples by their sampled Material's CODE, not just its name. A Sample carries only
		// IDMaterial (no code column of its own), so reach through the foreign-key picker: widen the
		// material picker's search columns to the same code fields the Materials list search uses
		// (Material Code / Catalog Code / Item Code). Mirrors the Materials list search fix.
		Filters:
		{
			IDMaterial: { ExternalFilterByColumns: [ 'Name', 'NameAbbreviated', 'MaterialCode', 'CatalogCode', 'ItemCode' ] }
		},
		// Curated list columns, matching the LIMS Samples list operators know: LabControlNumber shows as "PSID",
		// Name as "Description", ReceivedFrom as "Source", and the Status CODE is resolved to its friendly label via
		// the {~SampleStatusName:~} MetaTemplate (registered in PlatformConfiguration-Application-Web). Setting Columns
		// here wins over the generated catalog ListColumns (the catalog only fills RecordSetListColumns when unset).
		Columns:
		[
			{ Key: 'LabControlNumber', DisplayName: 'PSID' },
			{ Key: 'Name', DisplayName: 'Description' },
			{ Key: 'Status', DisplayName: 'Status', PictDashboard: { ValueTemplate: '{~SampleStatusName:Record.Payload.Status~}' } },
			{ Key: 'ReceivedFrom', DisplayName: 'Source' },
			// stopPropagation keeps a row click (where a list has one) from also firing, so a cmd-click opens one new tab.
			{ Key: 'QRCode', DisplayName: 'QR Code', PictDashboard: { ValueTemplate: '<a href="#/PSRS/Sample/View/{~D:Record.Payload.GUIDSample~}" onclick="event.stopPropagation()">{~D:Record.Payload.QRCode~}</a>' } },
			{ Key: 'LabReferenceNumber', DisplayName: 'Lab Reference #' },
			{ Key: 'ManufacturerCode', DisplayName: 'Manufacturer Code' },
		],
		// Status is a fixed vocabulary, not free text — offer the sample statuses as a labelled pick
		// list instead of the auto-generated free-entry Exact / Partial / Range inputs, so a filter
		// can't miss on a typo or wrong case. This mirrors the LIMS "Sample Status" filter exactly
		// (web/lims/config/LADOTD/sample-filter-manifest.json): a StringMatch (exact) clause rendered
		// as a Select2 whose { id, text } options show a friendly label while filtering on the stored
		// code. This is the HISTORICAL set, deliberately wider than what a sample form now offers: the
		// selectable vocabulary moved to the customer-managed SampleStatus table, but a filter still has
		// to reach samples stamped with a code since retired from that list.
		FieldFilterClauses:
		{
			Status:
			[
				{
					FilterKey: 'Status',
					ClauseKey: 'Status_Select_List',
					// Label heads the clause once added to the drawer; DisplayName is the button under the
					// "Status" field group in the Add-Filter popover (sibling to "Exact Match" on other fields).
					Label: 'Status',
					DisplayName: 'Is',
					Type: 'StringMatch',
					FilterByColumn: 'Status',
					ExactMatch: true,
					DataType: 'String',
					Ordinal: 1,
					PictForm:
					{
						InputType: 'Select2',
						SelectOptions:
						[
							{ id: 'N/A',  text: 'N/A' },
							{ id: 'LOGD', text: 'Logged' },
							{ id: 'RECV', text: 'Received' },
							{ id: 'INFO', text: 'Info' },
							{ id: 'PREA', text: 'Preauthorized' },
							{ id: 'PEND', text: 'Pending' },
							{ id: 'TEST', text: 'In Testing' },
							{ id: 'TSTR', text: 'Test Run' },
							{ id: 'FAIL', text: 'Failed' },
							{ id: 'FAIC', text: 'Failed with Comments' },
							{ id: 'PASS', text: 'Passed' },
							{ id: 'PASC', text: 'Passed with Comments' },
							{ id: 'VOID', text: 'Voided' }
						]
					}
				}
			]
		}
	},
	{
		Entity: 'SampleStatus',
		Label: 'Sample Statuses',
		Domain: 'Tests & Specifications',
		Icon: 'fas fa-clipboard-list',
		Keywords: 'sample status code state workflow logged received pending testing passed failed voided',
		Description: 'The sample statuses this customer can set on a sample.',
		// Admins look a status up by its code as often as by its label - the code is what shows on
		// exports and what the form field persists into Sample.Status.
		SearchFields: [ 'Name', 'Hash' ]
	},
	{
		Entity: 'Core',
		Label: 'Cores',
		Domain: 'Tests & Specifications',
		Icon: 'fas fa-circle-dot',
		Keywords: 'core sample drilled pavement',
		Description: 'Core samples.'
	},

	// --- Added so they register as recordsets and can anchor / target generated connections ---
	{
		Entity: 'LineItem',
		Label: 'Line Items',
		Domain: 'Projects & Contracts',
		Icon: 'fas fa-list-ol',
		Keywords: 'line item contract bid pay quantity contractor',
		Description: 'Contract line items — quantities, pay items, materials, and contractor links.',
		// Surface the operational scope (Contract / Project) and the mapped Bid Item as named columns.
		// IDContract / IDProject / IDBidItem render as their entity names via PSRS_FOREIGN_KEY_ENTITIES
		// (App-Web), which also makes them first-class entity-picker filters in the filter drawer.
		Columns:
		[
			{ Key: 'Name', DisplayName: 'Name' },
			{ Key: 'ItemNumber', DisplayName: 'Item Number' },
			{ Key: 'IDBidItem', DisplayName: 'Bid Item' },
			{ Key: 'IDContract', DisplayName: 'Contract' },
			{ Key: 'IDProject', DisplayName: 'Project' },
			{ Key: 'Status', DisplayName: 'Status' }
		]
	},
	{
		Entity: 'BidItem',
		Label: 'Bid Items',
		Domain: 'Projects & Contracts',
		Icon: 'fas fa-gavel',
		Keywords: 'bid item contract proposal award organization',
		Description: 'Contract bid items and their bidding organizations.',
		// Contract / Project (+ bidding Organization) render as entity names + first-class picker filters
		// via PSRS_FOREIGN_KEY_ENTITIES (App-Web).
		Columns:
		[
			{ Key: 'Name', DisplayName: 'Name' },
			{ Key: 'Description', DisplayName: 'Description' },
			{ Key: 'IDContract', DisplayName: 'Contract' },
			{ Key: 'IDProject', DisplayName: 'Project' },
			{ Key: 'Quantity', DisplayName: 'Quantity' },
			{ Key: 'IDOrganization', DisplayName: 'Organization' }
		]
	},
	{
		Entity: 'TestSpecificationRequirement',
		Label: 'Spec Requirements',
		Domain: 'Tests & Specifications',
		Icon: 'fas fa-clipboard-check',
		Keywords: 'test specification requirement threshold limit method',
		Description: 'Individual requirements within a test specification.'
	},
	{
		Entity: 'AssetType',
		Label: 'Asset Types',
		Domain: 'Physical Assets',
		Icon: 'fas fa-shapes',
		Keywords: 'asset type catalog bridge culvert sign physical infrastructure',
		Description: 'Types of physical asset (bridge, culvert, sign, …).'
	},
	{
		Entity: 'AssetClassification',
		Label: 'Asset Classifications',
		Domain: 'Physical Assets',
		Icon: 'fas fa-sitemap',
		Keywords: 'asset classification category physical infrastructure',
		Description: 'Classification buckets for physical assets.'
	},
	{
		Entity: 'PhysicalAssetElementType',
		Label: 'Asset Element Types',
		Domain: 'Physical Assets',
		Icon: 'fas fa-diagram-project',
		Keywords: 'element type aashto nbe section code unit bridge deck girder railing joint',
		Description: 'AASHTO element-type catalog (the element library) for asset inspection.',
		SearchFields: [ 'Name', 'Code', 'Section', 'SectionCode' ],
		// Which AASHTO defects apply to this element type (populated by the catalog Sync).
		ReadTabs: [ { Association: 'PhysicalAssetElementTypeDefect', Title: 'Defects' } ]
	},
	{
		Entity: 'PhysicalAssetMaterial',
		Label: 'Asset Materials',
		Domain: 'Physical Assets',
		Icon: 'fas fa-layer-group',
		Keywords: 'asset material element concrete steel prestressed reinforced timber masonry',
		Description: 'Materials an asset element is made of (AASHTO material classes).',
		// The friendly Material text column is skipped by the generator's list/picker heuristics — curate it.
		Columns:
		[
			{ Key: 'Code', DisplayName: 'Code' },
			{ Key: 'Material', DisplayName: 'Material' },
			{ Key: 'Description', DisplayName: 'Description' }
		],
		SearchFields: [ 'Code', 'Material', 'Description' ]
	},
	{
		Entity: 'PhysicalAssetDefect',
		Label: 'Asset Defects',
		Domain: 'Physical Assets',
		Icon: 'fas fa-triangle-exclamation',
		Keywords: 'defect aashto condition state corrosion cracking connection damage distortion',
		Description: 'AASHTO defect catalog with per-condition-state descriptions.',
		SearchFields: [ 'Name', 'Code' ],
		// The element types this defect applies to (reciprocal of the element-type Defects tab).
		ReadTabs: [ { Association: 'PhysicalAssetElementTypeDefect', Title: 'Element Types' } ]
	},
	{
		Entity: 'PhysicalAsset',
		Label: 'Physical Assets',
		Domain: 'Physical Assets',
		Icon: 'fas fa-cube',
		Keywords: 'physical asset bridge culvert instance inventory structure',
		Description: 'The physical asset inventory (bridges, culverts, …). Open one for its workspace.',
		Columns:
		[
			{ Key: 'Name', DisplayName: 'Name' },
			{ Key: 'AssetNumber', DisplayName: 'Asset #' },
			{ Key: 'ExternalSyncGUID', DisplayName: 'Structure' },
			{ Key: 'YearBuilt', DisplayName: 'Year Built' }
		],
		SearchFields: [ 'Name', 'AssetNumber', 'ExternalSyncGUID' ],
		// Externally synced — not hand-authored here. Hides the list "+ New"; the router sends the generic
		// Edit route to the (view-only) workspace and Create back to the list.
		NoCreate: true
	},
	{
		Entity: 'Inspection',
		Label: 'Inspections',
		Domain: 'Physical Assets',
		Icon: 'fas fa-clipboard-check',
		Keywords: 'inspection bridge culvert routine NSTM underwater scour scheduled dossier condition survey field asset',
		Description: 'Scheduled and dynamic asset inspections — open one for its workspace (assets, document dossier, inspectors, timeline).',
		Columns:
		[
			{ Key: 'Name', DisplayName: 'Name' },
			{ Key: 'StatusDisplay', DisplayName: 'Status' },
			{ Key: 'ScheduledStartDate', DisplayName: 'Scheduled' },
			{ Key: 'DeadlineDate', DisplayName: 'Deadline' }
		],
		SearchFields: [ 'Name', 'Description' ]
		// "+ New" opens the bespoke schedule screen: the router bridges /PSRS/Inspection/Create to
		// InspectionCreateWorkspace, which creates the asset join, the project scope and the document packet
		// through BridgeDocumentManager.createInspection(). (NoCreate was set here while only the generic
		// raw-schema form existed — that form produced inspections with no asset, project or documents.)
	}
];

// Duplicate read-tab resolution. The schema-graph generator (Connections.generated.js) emits BOTH a
// direct-FK child list AND a join-table association for an entity pair that has both, so three read views
// showed two tabs with the SAME title (audited 2026-09-05, verified against live data). Resolve each per
// the data + the product decision:
//   • LineItem "Samples": the FK Sample.IDLineItem is effectively unpopulated (1 row app-wide) so the child
//     list is always empty — drop it; the LineItemSample join association (SampleLineItemJoin) holds the data.
//   • TestSpecificationSet "Pay Items": the FK child list and the join association return the IDENTICAL set
//     (redundant) — drop the child list; keep the PayItemTestSpecificationSet association.
//   • Lab "Test Instances": the FK child list (TestInstance.IDLab — the instance's primary lab, ~838k) and
//     the join association (TestInstanceLabJoin, ~380k) are DIFFERENT and both real — keep BOTH, and rename
//     the join tab to "Linked Test Instances" so the two are distinguishable.
const READ_TAB_OVERRIDES =
{
	LineItem:             { DropChildLists: [ { RecordSet: 'Sample',  FilterField: 'IDLineItem' } ] },
	TestSpecificationSet: { DropChildLists: [ { RecordSet: 'PayItem', FilterField: 'IDTestSpecificationSet' } ] },
	Lab:                  { RenameReadTabs: [ { Association: 'LabTestInstance', Title: 'Linked Test Instances' } ] },
};

/**
 * Resolve the duplicate read tabs described by READ_TAB_OVERRIDES: drop the named child lists and/or rename
 * the named association tabs. Clones only the arrays/entries it changes, so the shared generated connection
 * objects (EntityConnections) are never mutated.
 * @param {Object} pEntry - a catalog entry (post applyConnectionDefaults).
 * @return {Object} the entry with duplicate tabs resolved.
 */
function applyReadTabOverrides(pEntry)
{
	const tmpOverride = READ_TAB_OVERRIDES[pEntry.Entity];
	if (!tmpOverride) { return pEntry; }
	const tmpResult = Object.assign({}, pEntry);
	if (Array.isArray(tmpOverride.DropChildLists) && Array.isArray(tmpResult.ChildLists))
	{
		tmpResult.ChildLists = tmpResult.ChildLists.filter((pChild) =>
			!tmpOverride.DropChildLists.some((pDrop) => (pDrop.RecordSet === pChild.RecordSet) && (pDrop.FilterField === pChild.FilterField)));
	}
	if (Array.isArray(tmpOverride.RenameReadTabs) && Array.isArray(tmpResult.ReadTabs))
	{
		tmpResult.ReadTabs = tmpResult.ReadTabs.map((pTab) =>
		{
			const tmpRename = tmpOverride.RenameReadTabs.find((pRename) => pRename.Association === pTab.Association);
			return tmpRename ? Object.assign({}, pTab, { Title: tmpRename.Title }) : pTab;
		});
	}
	return tmpResult;
}

module.exports = CATALOG.map(applyConnectionDefaults).map(applyReadTabOverrides);
